/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { Fragment, useContext, useState } from "react";
import { SimpliCityAdminSDK } from "@simplicitycms/simplicity-admin-js-sdk";
import { Dialog, CardContent, Card } from "@mui/material";
import { useSnackbar } from "hooks/useSnackbar";
import { ImageEditContext } from "cms/images/image-selector/context/image-edit-context";
import { GalleryContext } from "cms/images/image-selector/dialog-select/context/gallery-context";
import { ImageEditHeader } from "cms/images/image-selector/dialog-edit/panels/image-edit-header";
import { ImageDialogBottomActions } from "cms/images/image-selector/dialog-edit/actions/image-dialog-bottom-actions";
import { ImageDialogPreviewPanel } from "cms/images/image-selector/dialog-edit/panels/image-dialog-preview-panel";
import { ImageEditPanel } from "cms/images/image-selector/dialog-edit/panels/image-edit-panel";
import { Logger } from "cms/utils/log/logger";
import { ImageSelectorClientContext } from "cms/images/image-selector/context/image-selector-client-context";
import { useSimplicityImage } from "cms/images/hooks/use-simplicity-image";
import { ImageEditArchivePanel } from "./panels/image-edit-archive-panel";
import { ImageEditReplacePanel } from "./panels/image-edit-replace-panel";
import { ImageRenamePanel } from "./panels/image-rename-panel";

// TODO: Do not import CSS directly.
import "./index.css";

const ImageEditDialog = () => {

    const { showSnackbar } = useSnackbar();
    const [images, setImages] = useState();
    const searchFunction = SimpliCityAdminSDK.image.search;
    const [loading, setLoading] = useState(false);
    const scrollableHeight = "480px";
    const { archiveImage } = useSimplicityImage();
    const [fileNameExist, setFileNameExist] = useState(false);
    const [renameFileNameValid, setRenameFileNameValid] = useState(true);

    const imageEditContext = useContext(ImageEditContext);
    const openEdit = imageEditContext?.openEdit;
    const handleEditClose = imageEditContext?.handleEditClose;
    const selectedImage = imageEditContext?.selectedImage;
    const setSelectedImage = imageEditContext?.setSelectedImage;
    const setOpen = imageEditContext?.setOpen;
    const isArchiving = imageEditContext?.isArchiving;
    const setIsArchiving = imageEditContext?.setIsArchiving;
    const setOpenEdit = imageEditContext?.setOpenEdit;
    const isReplacing = imageEditContext?.isReplacing;
    const onImageArchived = imageEditContext?.onImageArchived;

    const isEdit = !isArchiving && !isReplacing;
    const hasFooter = !isArchiving && !isReplacing;

    const onSelectImage = (event, image) => {
        // showSnackbar("onSelectImage")
        event.stopPropagation();
        setSelectedImage(image);
    };
    const handleCancel = (e) => {
        setFileNameExist(false);
        setRenameFileNameValid(false);
        handleEditClose(e);
    };

    const galleryCTX = {
        images,
        setImages,
        searchFunction,
        scrollableHeight,
        onSelectImage,
        selectedImage, // from edit context
        setSelectedImage, // from edit context
        showSnackbar,
    };
    
    const handleReload = async (image) => {
        Logger.debug("[ImageEditDialog] onReload...");
        setSelectedImage(image);
    };
    
    const imageSelectorClientContext = {
        handleReload,
    };

    const handleArchive = async () => {
        setLoading(true);
        const archived_image_status = await archiveImage(selectedImage?.guid);
        archived_image_status === "archived"
            ? showSnackbar("Image Archived.")
            : showSnackbar("Failed to archive the image. Please try again.");
        setIsArchiving(false);
        setSelectedImage(archived_image_status);
        if (typeof onImageArchived === "function") {
            onImageArchived();
        }
        setOpenEdit(false);
        setOpen(false);
        setLoading(false);
    };

    return (
        <>
            <ImageSelectorClientContext.Provider value={imageSelectorClientContext}>
                <Dialog
                    open={openEdit}
                    onClose={handleCancel}
                    fullWidth={true}
                    maxWidth="md"
                    className="simplicity-image-selector-overlay"
                >
                    <GalleryContext.Provider value={galleryCTX}>
                        {/* simplicity-image-selector-window sets the dialog size */}
                        <Card
                            elevation={0}
                            className="simplicity-image-edit-window dialog-edit-window"
                        >
                            <CardContent sx={{ flex: "1 0 auto", height: "100%" }}>
                                
                                <ImageEditHeader />

                                <ImageRenamePanel
                                    fileNameExist={fileNameExist}
                                    setFileNameExist={setFileNameExist}
                                    renameFileNameValid={renameFileNameValid}
                                    setRenameFileNameValid={setRenameFileNameValid}
                                    setLoading={setLoading}
                                    loading={loading}
                                />

                                <div className="simplicity-image-edit-window-content">
                                    
                                    {isArchiving && (
                                        <ImageEditArchivePanel
                                            handleArchive={handleArchive}
                                            loading={loading}
                                        />
                                    )}
                                    
                                    {isReplacing &&
                                        <ImageEditReplacePanel />
                                    }
                                    
                                    {isEdit && (
                                        <>
                                            <ImageDialogPreviewPanel />
                                            <div className="simplicity-image-selector-window-active image-edit-panel">
                                                <ImageEditPanel />
                                            </div>
                                        </>
                                    )}
                                    
                                </div>
                            </CardContent>
                        </Card>
                        <div className={`footer-wrapper ${!fileNameExist && !renameFileNameValid ? "error-exist-footer" : ""}`}>
                            {fileNameExist && (
                                <div className="image-file-name-exist">
                                    Error: Filename already exists
                                </div>
                            )}
                            {!renameFileNameValid && (
                                <div className="image-file-name-invalid">
                                    Error: filename doesn’t meet requirements, eg. one-two2-three-4.jpg
                                </div>
                            )}
                                
                            {hasFooter &&
                                <ImageDialogBottomActions />
                            }
                        </div>

                    </GalleryContext.Provider>
                </Dialog>
            </ImageSelectorClientContext.Provider>
        </>
    );
};

export { ImageEditDialog };
